import React from "react";
import styles from "./ThresholdTogglesPopover.module.css";

/**
 * ThresholdTogglesPopover — body of the "Set Reading Thresholds" popover
 * triggered by the inline link in the chart panel header. Pixel-perfect
 * reference: Figma "RPM TOCA AI Patient Dashboard" — Reading Thresholds
 * popover (image 28).
 *
 * Props:
 *   - readingType: string — drives which set of toggles renders. Mirrors
 *                  the existing `READING_TYPES` constants in HealthChart.
 *   - thresholds:  object — current toggle state. Same shape used by
 *                  HealthChart's internal `thresholds` state.
 *   - onToggle:    (key) => void — flip the boolean for `key`.
 *   - onClose:     () => void — Save button calls this to dismiss.
 *   - thresholdValues: object — the matching slice of CONFIG.thresholdValues
 *                  for the active readingType (so the popover can label
 *                  each row with its numeric threshold).
 *   - unit:        string — "mmHg" / "mg/dL" / "kg" / "°F" / "%"
 *
 * Persistence is left to the parent (per the plan, the parent writes
 * preferences to localStorage on `onClose`).
 */
const ThresholdTogglesPopover = ({
  readingType,
  thresholds,
  onToggle,
  onClose,
  thresholdValues,
  unit,
}) => {
  const rows = getRowsForType(readingType, thresholdValues, unit);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h4 className={styles.heading}>Reading Thresholds</h4>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close thresholds popover"
        >
          ×
        </button>
      </div>

      <div className={styles.rows}>
        {rows.map((row) => (
          <label key={row.key} className={styles.row}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={Boolean(thresholds?.[row.key])}
              onChange={() => onToggle(row.key)}
              aria-label={`Toggle ${row.label}`}
            />
            <span className={styles.label}>{row.label}</span>
          </label>
        ))}
        {rows.length === 0 && (
          <p className={styles.empty}>
            No thresholds available for this reading type.
          </p>
        )}
      </div>

      <button type="button" className={styles.saveBtn} onClick={onClose}>
        Save
      </button>
    </div>
  );
};

/** Build the list of rows for a given reading type, pulling values from
 *  the `CONFIG.thresholdValues[readingType]` slice. Each row maps to a
 *  key in the `thresholds` state object. */
function getRowsForType(readingType, vals, unit) {
  if (!vals) return [];
  const u = unit || "";
  switch (readingType) {
    case "blood_pressure":
      return [
        { key: "systolicLow", label: `Systolic Low (${vals.systolicLow} ${u})` },
        { key: "systolicHigh", label: `Systolic High (${vals.systolicHigh} ${u})` },
        { key: "diastolicLow", label: `Diastolic Low (${vals.diastolicLow} ${u})` },
        { key: "diastolicHigh", label: `Diastolic High (${vals.diastolicHigh} ${u})` },
      ];
    case "weight":
      return [
        { key: "low", label: `Weight Low (${vals.low} ${u})` },
        { key: "high", label: `Weight High (${vals.high} ${u})` },
      ];
    case "temperature":
      return [
        { key: "low", label: `Low Temp (${vals.low}${u})` },
        { key: "high", label: `Fever (${vals.high}${u})` },
      ];
    case "pulse_ox":
      return [
        { key: "oxygenLow", label: `Oxygen Low (${vals.oxygenLow}${u})` },
      ];
    case "blood_glucose":
      return [
        { key: "low", label: `Glucose Low (${vals.low} ${u})` },
        { key: "high", label: `Glucose High (${vals.high} ${u})` },
      ];
    default:
      return [];
  }
}

export default ThresholdTogglesPopover;
